import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';
import ContentCaption from './content_caption';
import TextSection from './text_section';
import SocialIcons from '../common/social_icons';
import MediaQuery, { MEDIA_QUERIES } from '../common/media_query';
import ProductLink from '../common/product_link';
import ResponsiveImg from '../common/responsive_img';
import Lazy from '../common/lazy';
import {
  getProportionalHeight
} from '../../utils/images';
import {
  DESKTOP_FULL_WIDTH_SLIDESHOW_CONTAINER_WIDTH_PX,
  DESKTOP_SLIDESHOW_CONTAINER_WIDTH_PX,
  MOBILE_SLIDESHOW_LAZY_LOAD_CUSHION_PX,
  NON_MOBILE_GRID_WIDTH_PX,
  FLOATING_IMAGE_MARGIN_PX
} from '../../constants';
import {isEmpty, reduce} from 'lodash';
import { link_target, link_rel } from '../../utils/external_links';

const RETINA_MULTIPLIER = 2;

const containerWidth = (isFullWidth: bool): number => {
  return isFullWidth
    ? DESKTOP_FULL_WIDTH_SLIDESHOW_CONTAINER_WIDTH_PX
    : DESKTOP_SLIDESHOW_CONTAINER_WIDTH_PX;
};

export default class ImageSection extends Component {
  state = { isLoaded: false };

  UNSAFE_componentWillReceiveProps = (nextProps) => {
    if (this.props.content.resource !== nextProps.content.resource) {
      this.setState({ isLoaded: false });
    }
  };

  handleImgLoad = () =>
    this.setState({ isLoaded: true });

  imgClassNames = (names) =>
    classNames({ ...names, loading: !this.state.isLoaded });

  responsiveImg = (dimensions) => (
    <ResponsiveImg src={this.props.content.resource}
      alt={this.props.content.alt_title}
      {...dimensions}
      original_width_px={this.props.content.dimensions.original_width_px}
      original_height_px={this.props.content.dimensions.original_height_px}
      className={this.imgClassNames()}
      onLoad={this.handleImgLoad}
    />
  );

  nonResponsiveImg = () => (
    <div className={this.imgClassNames({ 'full-width': true })}>
      <img alt={this.props.content.alt_title} src={this.props.content.resource} onLoad={this.handleImgLoad} />
    </div>
  );

  /*
   * Older slideshow articles sometimes contain images having widths
   * insufficiently large to fill the slide container. In these cases, we want
   * to bypass our ResponsiveImg component entirely and simply defer to the
   * full-sized image.
   */
  desktopSlideshowImg = (dimensions) => {
    const {
      content,
      isFullWidthSlideSection
    } = this.props;

    const width = containerWidth(isFullWidthSlideSection);

    return content.dimensions.original_width_px < (width * RETINA_MULTIPLIER)
      ? this.nonResponsiveImg()
      : this.responsiveImg(dimensions);
  }

  img = (dimensions) =>
    this.props.isDesktopSlideSection
      ? this.desktopSlideshowImg(dimensions)
      : this.responsiveImg(dimensions);

  innerComponents(breakpoint) {
    const { url_full, sanitizedTitle, enableAdTextForShares,
      meta_description, meta_title, social_title, excerpt_truncated_325,
      isMobileSlideSection, isFullWidthSlideSection, isDesktopSlideSection
    } = this.props;

    const { credit, description, resource, alignment, is_pinnable,
      is_facebook_shareable, dimensions, products,
      clicktracker, width_percentage } = this.props.content;

    const productLinkProps = ({ sponsor_info, external_full_path }) => ({
      click_tracker: sponsor_info && sponsor_info.click_tracker_url,
      product_url: external_full_path
    });

    let socialIcons;
    let lazyWrappedImg;
    if (!isEmpty(resource)) {
      const socialIconProps = {
        url_full, asset_main: { src_full: resource }, sanitizedTitle,
        enableAdTextForShares, excerpt_truncated_325, meta_description,
        meta_title, social_title
      };
      const icons = getIconsArray(is_pinnable, is_facebook_shareable);
      socialIcons = (
        <SocialIcons {...socialIconProps}
          className="image-shares"
          icons={icons}
        />
      );

      let imgDimensions;
      switch (breakpoint) {
        case 'desktop':
          imgDimensions = desktopImageDimensions(
            dimensions, alignment, width_percentage,
            isFullWidthSlideSection, isDesktopSlideSection
          );
          break;
        case 'tablet':
          imgDimensions = tabletImageDimensions(
            dimensions, alignment, width_percentage
          );
          break;
        case 'mobile':
          imgDimensions = mobileImageDimensions(dimensions, width_percentage);
      }

      const img = this.img(imgDimensions);
      const lazyLoadCushionPx = isMobileSlideSection
        ? MOBILE_SLIDESHOW_LAZY_LOAD_CUSHION_PX
        : null;

      let wrappedImg = <a href={clicktracker} target={link_target(clicktracker)} rel={link_rel(clicktracker)}>{img}</a>;
      if (isEmpty(clicktracker)) {
        wrappedImg = products[0]
          ? <ProductLink {...productLinkProps(products[0])}>{img}</ProductLink>
          : img;
      }

      lazyWrappedImg = (
        <Lazy className={classNames({ loading: !this.state.isLoaded })}
          cushion={lazyLoadCushionPx}
        >{wrappedImg}</Lazy>
      );
    }

    const contentCaption = isMobileSlideSection
      ? <ContentCaption credit={credit} />
      : <ContentCaption credit={credit} description={description} />;

    return (
      <div>
        <div className="img-container">
          {lazyWrappedImg}
        </div>
        {socialIcons}
        {contentCaption}
      </div>
    );
  }

  render() {
    const {
      isMobileSlideSection,
      isDesktopSlideSection,
      isFullWidthSlideSection,
      content
    } = this.props;

    const {
      description, alignment, width_percentage, dimensions
    } = content;

    const textSection = isMobileSlideSection
      ? <TextSection content={description} />
      : null;

    const mobileForcedStyle = shouldUseMobileForcedStyle(width_percentage)
      ? { maxWidth: Math.floor(mobileImageDimensions(dimensions).width) + 'px' }
      : {};

    const tabletForcedStyle =
      shouldUseNonMobileForcedStyle(width_percentage, false)
        ? { width: Math.floor(tabletImageDimensions(
          dimensions, alignment, width_percentage
        ).width) + 'px' }
        : {};

    const desktopForcedStyle =
      shouldUseNonMobileForcedStyle(width_percentage, isDesktopSlideSection)
        ? { width: Math.floor(desktopImageDimensions(
          dimensions, alignment, width_percentage,
          isFullWidthSlideSection, isDesktopSlideSection
        ).width) + 'px' }
        : {};

    return (
      <div className="section-image-container">
        <MediaQuery breakpoint="mobile">
          <div className={classNames('section-image')} style={mobileForcedStyle}>
            {this.innerComponents('mobile')}
          </div>
        </MediaQuery>
        <MediaQuery breakpoint="tablet">
          <div className="section-image" style={tabletForcedStyle}>
            {this.innerComponents('tablet')}
          </div>
        </MediaQuery>
        <MediaQuery breakpoint="desktop">
          <div className="section-image" style={desktopForcedStyle}>
            {this.innerComponents('desktop')}
          </div>
        </MediaQuery>
        {textSection}
      </div>
    );
  }
}

ImageSection.propTypes = {
  sanitizedTitle: PropTypes.string.isRequired,
  enableAdTextForShares: PropTypes.bool,
  url_full: PropTypes.string.isRequired,
  excerpt_truncated_325: PropTypes.string.isRequired,
  content: PropTypes.shape({
    credit: PropTypes.string,
    description: PropTypes.string,
    alt_title: PropTypes.string,
    resource: PropTypes.string.isRequired,
    alignment: PropTypes.string.isRequired,
    width_percentage: PropTypes.string,
    is_pinnable: PropTypes.bool.isRequired,
    is_facebook_shareable: PropTypes.bool.isRequired,
    dimensions: PropTypes.shape({
      original_height_px: PropTypes.number.isRequired,
      original_width_px: PropTypes.number.isRequired
    }).isRequired,
    clicktracker: PropTypes.string,
    products: PropTypes.array
  }).isRequired,
  meta_description: PropTypes.string,
  meta_title: PropTypes.string,
  social_title: PropTypes.string,
  isFullWidthSlideSection: PropTypes.bool,
  isMobileSlideSection: PropTypes.bool,
  isDesktopSlideSection: PropTypes.bool,
  polyptychAttributes: PropTypes.shape({
    isLastInPolyptych: PropTypes.bool.isRequired
  })
};

ImageSection.defaultProps = {
  content: {},
  isFullWidthSlideSection: false,
  isMobileSlideSection: false,
  isDesktopSlideSection: false
};

/*
 * If this returns true, the result of mobileImageDimensions is used for
 * both image resizing AND is inserted as a 'max-width' inline style.
 * If it returns false, mobileImageDimensions is used for image resizing only.
 */
function shouldUseMobileForcedStyle(width_percentage) {
  return width_percentage !== 'PAGE';
}

/*
 * If this returns true, the result of `(tablet|desktop)ImageDimensions` is used for
 * both image resizing AND is inserted as a 'width' inline style.
 * If it returns false, `(tablet|desktop)ImageDimensions` is used for image
 * resizing only.
 */
function shouldUseNonMobileForcedStyle(
  width_percentage,
  isDesktopSlideSection
) {
  const createdInOldStoryEditor = !width_percentage;
  return createdInOldStoryEditor && !isDesktopSlideSection;
}

function mobileImageDimensions(originalDimensions, width_percentage) {
  const width = Math.min(
    originalDimensions.original_width_px,
    width_percentage === 'PAGE'
      ? MEDIA_QUERIES.mobile.maxWidth // request a max of 760 if it's full bleed
      : NON_MOBILE_GRID_WIDTH_PX // request a max of 720 if it's not
  );
  return { width, height: getProportionalHeight(width, originalDimensions) };
}

function tabletImageDimensions(
  originalDimensions, alignment, width_percentage
) {
  if (width_percentage === 'PAGE') {
    const width = Math.min(
      originalDimensions.original_width_px,
      MEDIA_QUERIES.tablet.maxWidth
    );
    return { width, height: getProportionalHeight(width, originalDimensions) };
  }

  return nonFullBleedDimensions(
    originalDimensions, alignment, width_percentage
  );
}

function desktopImageDimensions(
  originalDimensions, alignment, width_percentage,
  isFullWidthSlideSection, isDesktopSlideSection
) {
  if (isDesktopSlideSection) {
    const width = Math.min(
      originalDimensions.original_width_px,
      containerWidth(isFullWidthSlideSection)
    );
    return {
      width,
      height: getProportionalHeight(width, originalDimensions)
    };
  }

  if (width_percentage === 'PAGE') {
    return {
      width: 1800, height: getProportionalHeight(1800, originalDimensions)
    };
  }

  return nonFullBleedDimensions(
    originalDimensions, alignment, width_percentage
  );
}

function nonFullBleedDimensions(
  originalDimensions, alignment, width_percentage
) {
  const isLeftOrRight = alignment === 'left' || alignment === 'right';
  const thirdWidth = isLeftOrRight
    ? NON_MOBILE_GRID_WIDTH_PX / 3 - FLOATING_IMAGE_MARGIN_PX
    : NON_MOBILE_GRID_WIDTH_PX / 3;
  const halfWidth = isLeftOrRight
    ? NON_MOBILE_GRID_WIDTH_PX / 2 - FLOATING_IMAGE_MARGIN_PX
    : NON_MOBILE_GRID_WIDTH_PX / 2;

  switch (width_percentage) {
    case 'THIRD':
      return {
        width: thirdWidth,
        height: getProportionalHeight(thirdWidth, originalDimensions)
      };
    case 'HALF':
      return {
        width: halfWidth,
        height: getProportionalHeight(halfWidth, originalDimensions)
      };
    case 'FULL':
      return {
        width: NON_MOBILE_GRID_WIDTH_PX,
        height: getProportionalHeight(
          NON_MOBILE_GRID_WIDTH_PX, originalDimensions
        )
      };
  }

  // Old story editor case
  const minOfWidthAndGrid = Math.min(
    originalDimensions.original_width_px,
    NON_MOBILE_GRID_WIDTH_PX
  );
  const width = isLeftOrRight
    ? minOfWidthAndGrid - FLOATING_IMAGE_MARGIN_PX
    : minOfWidthAndGrid;
  return {
    width,
    height: getProportionalHeight(width, originalDimensions)
  };
}

function getIconsArray(is_pinnable, is_facebook_shareable) {
  const iconsDict = {
    pinterest: is_pinnable,
    facebook: is_facebook_shareable
  };
  return reduce(iconsDict, (icons, shouldRenderIcon, icon) =>
    shouldRenderIcon ? [...icons, icon] : icons,
  []);
}
